// Draws a full-screen overlay (the checkout, the cart, a drawer, a modal, a
// toast) in a layer on <body> instead of inside the widget's own <div>.
//
// The widget's <div> sits wherever the venue put it on their page, and any
// ancestor of it that is positioned with a z-index (or has a transform,
// filter, contain…) is a stacking context the overlay cannot escape: its
// `position: fixed; z-index: 999999` then only counts INSIDE that ancestor's
// layer. On fijnland.co.za the Avada row around the widget is
// `position: relative; z-index: 10`, so the theme's sticky header (10011), its
// logo and its newsletter form all painted over our checkout. A transformed
// ancestor would also re-anchor `position: fixed` to itself, off the viewport.
// Appended to <body>, the overlay's z-index competes with the page's own.
//
// Inside the layer is a second `.vt-widget` carrying the root's theme
// variables and data attributes (from WidgetLayerScopeProvider), so every
// `.vt-widget …` / `[data-vt-layout] …` rule and every var(--vt-*) still
// applies; `.vt-widget.vt-layer` is display: contents, so it draws nothing
// itself. A brand-tinted panel (WidgetLayerVars) re-supplies its own variables.
//
// React events still bubble through the React tree (a click in the layer
// reaches the same handlers it did), but the DOM ancestry changes: code that
// asks "is this inside the widget?" with widgetRoot.contains(target) would
// see a click in the layer as outside. None does today; keep it that way, or
// check the layer too.
//
// A layer inside a layer (the gallery opened from the booking drawer) renders
// in place, as it always did: its DOM and stacking stay inside its parent's.
// Without a scope (a component test that mounts a panel on its own) it renders
// in place too.

import {
  createContext,
  useContext,
  useInsertionEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export interface WidgetLayerScope {
  /** The root's inline style: the theme's CSS variables. */
  style: CSSProperties;
  /** The root's data attributes (data-brand, data-vt-layout, theme flags). */
  attrs: Record<string, string>;
}

const ScopeContext = createContext<WidgetLayerScope | null>(null);
const InLayerContext = createContext(false);

export function WidgetLayerScopeProvider({
  value,
  children,
}: {
  value: WidgetLayerScope;
  children: ReactNode;
}) {
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

/**
 * Variables a part of the widget sets on itself (a panel's brand tint) and its
 * overlays would have inherited in place; layered over the root's.
 */
export function WidgetLayerVars({
  style,
  children,
}: {
  style: CSSProperties | undefined;
  children: ReactNode;
}) {
  const outer = useContext(ScopeContext);
  const value = useMemo(
    () => (outer && style ? { ...outer, style: { ...outer.style, ...style } } : outer),
    [outer, style],
  );
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function WidgetLayer({ children }: { children: ReactNode }) {
  const scope = useContext(ScopeContext);
  const nested = useContext(InLayerContext);
  const portaled = scope !== null && !nested;
  const [host] = useState(() =>
    typeof document === "undefined" ? null : document.createElement("div"),
  );

  // useInsertionEffect, not useLayoutEffect: the host must be in the document
  // before ANY layout effect of the overlay runs — children's effects run
  // before their parent's, and the date popover measures its own height in a
  // layout effect to place itself (0 in a detached node).
  useInsertionEffect(() => {
    if (!portaled || !host) return;
    // display: contents on the host too — it is outside .vt-widget, where a
    // theme rule like `body > div { position: relative; z-index: 1 }` could
    // make it the very stacking context this escapes; with no box it can't.
    host.style.display = "contents";
    host.setAttribute("data-vertu-layer", "");
    document.body.appendChild(host);
    return () => host.remove();
  }, [portaled, host]);

  if (!portaled || !host || !scope) return <>{children}</>;
  return createPortal(
    <InLayerContext.Provider value={true}>
      <div className="vt-widget vt-layer" style={scope.style} {...scope.attrs}>
        {children}
      </div>
    </InLayerContext.Provider>,
    host,
  );
}
